import { CONTROL_TYPES } from '../constants/assessmentTypes';

// Simplified response options for E8 Light Assessment
export const LIGHT_RESPONSES = {
  YES: 'yes',
  NO: 'no',
  NA: 'not_applicable',
  DONT_KNOW: 'dont_know'
};

// Response descriptions for E8 Light
export const LIGHT_RESPONSE_DESCRIPTIONS = {
  yes: 'Yes, this control is implemented.',
  no: 'No, this control is not implemented.',
  not_applicable: 'This control is not applicable to our environment.',
  dont_know: 'I don\'t know whether this control is implemented.'
};

// Mapping from light responses to regular assessment responses
export const mapLightToRegularResponse = (lightResponse) => {
  switch(lightResponse) {
    case LIGHT_RESPONSES.YES: return 'effective';
    case LIGHT_RESPONSES.NO: return 'not_implemented';
    case LIGHT_RESPONSES.NA: return 'not_applicable';
    case LIGHT_RESPONSES.DONT_KNOW: return 'no_visibility';
    default: return 'not_assessed';
  }
};

// Simplified questions for E8 Light Readiness, grouped by cumulative maturity level.
export const lightQuestions = {
  [CONTROL_TYPES.PATCH_APPLICATIONS]: {
    ML1: [
      { 
        id: "L-ML1-PA-01", 
        question: "Do you use an automated tool to discover IT assets at least every two weeks to identify what needs to be scanned for vulnerabilities?",
        originalId: "ML1-PA-01"
      },
      { 
        id: "L-ML1-PA-02", 
        question: "Do you use a vulnerability scanner with an up-to-date vulnerability database?",
        originalId: "ML1-PA-02"
      },
      { 
        id: "L-ML1-PA-03", 
        question: "Do you scan your online services (websites, cloud apps, etc.) daily for missing patches or updates?",
        originalId: "ML1-PA-03"
      },
      { 
        id: "L-ML1-PA-04", 
        question: "Do you scan office software, browsers, email clients, PDF readers, and security products weekly for missing patches?",
        originalId: "ML1-PA-04"
      },
      { 
        id: "L-ML1-PA-05", 
        question: "Do you apply critical patches or mitigations for online services within 48 hours when vulnerabilities are critical or exploits exist?",
        originalId: "ML1-PA-05"
      },
      { 
        id: "L-ML1-PA-06", 
        question: "Do you apply non-critical patches for online services within two weeks when no working exploits exist?",
        originalId: "ML1-PA-06"
      },
      { 
        id: "L-ML1-PA-07", 
        question: "Do you apply patches for office software, browsers, email clients, PDF readers, and security products within two weeks?",
        originalId: "ML1-PA-07"
      },
      { 
        id: "L-ML1-PA-08", 
        question: "Have you removed all unsupported online services that no longer receive security updates?",
        originalId: "ML1-PA-08"
      },
      { 
        id: "L-ML1-PA-09", 
        question: "Have you removed all unsupported office software, browsers, email clients, PDF software, Flash Player, and security products?",
        originalId: "ML1-PA-09"
      },
    ],
    ML2: [
      { 
        id: "L-ML2-PA-01", 
        question: "Do you scan all other applications (besides office software, browsers, etc.) at least every two weeks for missing patches?",
        originalId: "ML2-PA-01"
      },
      { 
        id: "L-ML2-PA-02", 
        question: "Do you apply patches for all other applications within one month of their release?",
        originalId: "ML2-PA-02"
      },
    ],
    ML3: [
      { 
        id: "L-ML3-PA-01", 
        question: "Do you apply critical patches for office software, browsers, email clients, PDF readers, and security products within 48 hours when vendors mark them as critical or when active exploits exist?",
        originalId: "ML3-PA-01"
      },
      { 
        id: "L-ML3-PA-02", 
        question: "Do you apply non-critical patches for office software, browsers, email clients, PDF readers, and security products within two weeks when no active exploits exist?",
        originalId: "ML3-PA-02"
      },
      { 
        id: "L-ML3-PA-03", 
        question: "Have you removed all applications that are no longer receiving security updates from vendors?",
        originalId: "ML3-PA-03"
      }
    ]
  },
  [CONTROL_TYPES.PATCH_OS]: {
    ML1: [
      { 
        id: "L-ML1-PO-01", 
        question: "Do you use automated tools to discover all your computers and servers at least every two weeks?",
        originalId: "ML1-PO-01"
      },
      { 
        id: "L-ML1-PO-02", 
        question: "Do you use a vulnerability scanner that's kept up-to-date with the latest security information?",
        originalId: "ML1-PO-02"
      },
      { 
        id: "L-ML1-PO-03", 
        question: "Do you scan your internet-facing servers and network devices daily for missing security updates?",
        originalId: "ML1-PO-03"
      },
      { 
        id: "L-ML1-PO-04", 
        question: "Do you scan your workstations and internal servers at least every two weeks for missing security updates?",
        originalId: "ML1-PO-04"
      },
      { 
        id: "L-ML1-PO-05", 
        question: "Do you apply critical security updates to your internet-facing servers and network devices within 48 hours when vendors mark them as critical or when active exploits exist?",
        originalId: "ML1-PO-05"
      },
      { 
        id: "L-ML1-PO-06", 
        question: "Do you apply non-critical security updates to your internet-facing servers and network devices within two weeks when no active exploits exist?",
        originalId: "ML1-PO-06"
      },
      { 
        id: "L-ML1-PO-07", 
        question: "Do you apply security updates to your workstations and internal servers within one month of release?",
        originalId: "ML1-PO-07"
      },
      { 
        id: "L-ML1-PO-08", 
        question: "Have you replaced all operating systems that are no longer receiving security updates from vendors?",
        originalId: "ML1-PO-08"
      }
    ],
    ML2: [], // No ML2 questions for Patch OS
    ML3: [
      { 
        id: "L-ML3-PO-01", 
        question: "Do you scan your device drivers at least every two weeks for missing security updates?",
        originalId: "ML3-PO-01"
      },
      { 
        id: "L-ML3-PO-02", 
        question: "Do you scan your device firmware at least every two weeks for missing security updates?",
        originalId: "ML3-PO-02"
      },
      { 
        id: "L-ML3-PO-03", 
        question: "Do you apply critical security updates to your workstations and internal servers within 48 hours when vendors mark them as critical or when active exploits exist?",
        originalId: "ML3-PO-03"
      },
      { 
        id: "L-ML3-PO-04", 
        question: "Do you apply non-critical security updates to your workstations and internal servers within one month when no active exploits exist?",
        originalId: "ML3-PO-04"
      },
      { 
        id: "L-ML3-PO-05", 
        question: "Do you apply critical security updates to your device drivers within 48 hours when vendors mark them as critical or when active exploits exist?",
        originalId: "ML3-PO-05"
      },
      { 
        id: "L-ML3-PO-06", 
        question: "Do you apply non-critical security updates to your device drivers within one month when no active exploits exist?",
        originalId: "ML3-PO-06"
      },
      { 
        id: "L-ML3-PO-07", 
        question: "Do you apply critical security updates to your device firmware within 48 hours when vendors mark them as critical or when active exploits exist?",
        originalId: "ML3-PO-07"
      },
      { 
        id: "L-ML3-PO-08", 
        question: "Do you apply non-critical security updates to your device firmware within one month when no active exploits exist?",
        originalId: "ML3-PO-08"
      },
      { 
        id: "L-ML3-PO-09", 
        question: "Do you use either the latest version or the previous version of operating systems across your organization?",
        originalId: "ML3-PO-09"
      }
    ]
  },
  [CONTROL_TYPES.MFA]: {
    ML1: [
      { 
        id: "L-ML1-MFA-01", 
        question: "Do you require multi-factor authentication (MFA) for accessing your organization's online services that handle sensitive data?",
        originalId: "ML1-MFA-01"
      },
      { 
        id: "L-ML1-MFA-02", 
        question: "Do you require MFA for accessing third-party online services that handle your organization's sensitive data?",
        originalId: "ML1-MFA-02"
      },
      { 
        id: "L-ML1-MFA-03", 
        question: "Do you use MFA (when available) for third-party services that handle your organization's non-sensitive data?",
        originalId: "ML1-MFA-03"
      },
      { 
        id: "L-ML1-MFA-04", 
        question: "Do you require MFA for staff accessing your organization's online customer services that handle sensitive customer data?",
        originalId: "ML1-MFA-04"
      },
      { 
        id: "L-ML1-MFA-05", 
        question: "Do you require MFA for accessing third-party online services that handle your sensitive customer data?",
        originalId: "ML1-MFA-05"
      },
      { 
        id: "L-ML1-MFA-06", 
        question: "Do you require MFA for customers accessing online services that handle their sensitive data?",
        originalId: "ML1-MFA-06"
      },
      { 
        id: "L-ML1-MFA-07", 
        question: "Does your MFA use either: something users have (like a phone or token) plus something they know (like a password), or something they have that's unlocked by something they know or are (like a fingerprint)?",
        originalId: "ML1-MFA-07"
      }
    ],
    ML2: [
      { 
        id: "L-ML2-MFA-01", 
        question: "Do you require MFA for all users with administrative access to your systems?",
        originalId: "ML2-MFA-01"
      },
      { 
        id: "L-ML2-MFA-02", 
        question: "Do you require MFA for all regular users of your systems?",
        originalId: "ML2-MFA-02"
      },
      { 
        id: "L-ML2-MFA-03", 
        question: "Do you use phishing-resistant MFA (like security keys or authenticator apps) for online services?",
        originalId: "ML2-MFA-03"
      },
      { 
        id: "L-ML2-MFA-04", 
        question: "Do you provide a phishing-resistant MFA option for customers of your online services?",
        originalId: "ML2-MFA-04"
      },
      { 
        id: "L-ML2-MFA-05", 
        question: "Do you use phishing-resistant MFA for system access?",
        originalId: "ML2-MFA-05"
      },
      { 
        id: "L-ML2-MFA-06", 
        question: "Do you log all successful and failed MFA attempts centrally?",
        originalId: "ML2-MFA-06"
      },
      { 
        id: "L-ML2-MFA-07", 
        question: "Are your MFA logs protected from unauthorized changes or deletion?",
        originalId: "ML2-MFA-07"
      },
      { 
        id: "L-ML2-MFA-08", 
        question: "Do you analyze MFA logs from internet-facing servers regularly to detect security issues?",
        originalId: "ML2-MFA-08"
      },
      { 
        id: "L-ML2-MFA-09", 
        question: "Do you analyze security events to identify security incidents?",
        originalId: "ML2-MFA-09"
      },
      { 
        id: "L-ML2-MFA-10", 
        question: "Do you report security incidents to your Chief Information Security Officer (or delegate) as soon as possible?",
        originalId: "ML2-MFA-10"
      },
      { 
        id: "L-ML2-MFA-11", 
        question: "Do you report security incidents to ASD as soon as possible?",
        originalId: "ML2-MFA-11"
      },
      { 
        id: "L-ML2-MFA-12", 
        question: "Do you follow your security incident response plan when incidents are identified?",
        originalId: "ML2-MFA-12"
      }
    ],
    ML3: [
      { 
        id: "L-ML3-MFA-01", 
        question: "Do you require MFA for accessing your data storage systems?",
        originalId: "ML3-MFA-01"
      },
      { 
        id: "L-ML3-MFA-02", 
        question: "Do you require phishing-resistant MFA for customers of your online services?",
        originalId: "ML3-MFA-02"
      },
      { 
        id: "L-ML3-MFA-03", 
        question: "Do you use phishing-resistant MFA for accessing your data storage systems?",
        originalId: "ML3-MFA-03"
      },
      { 
        id: "L-ML3-MFA-04", 
        question: "Do you analyze MFA logs from internal servers regularly to detect security issues?",
        originalId: "ML3-MFA-04"
      },
      { 
        id: "L-ML3-MFA-05", 
        question: "Do you analyze MFA logs from workstations regularly to detect security issues?",
        originalId: "ML3-MFA-05"
      }
    ]
  },
  [CONTROL_TYPES.ADMIN_PRIVILEGES]: {
    ML1: [
      { 
        id: "L-ML1-AP-01", 
        question: "Do you verify and approve requests for admin access to systems, applications, and data storage when they are first requested?",
        originalId: "ML1-RAP-01"
      },
      { 
        id: "L-ML1-AP-02", 
        question: "Do your admin users have separate accounts just for admin tasks, not for regular work?",
        originalId: "ML1-RAP-02"
      },
      { 
        id: "L-ML1-AP-03", 
        question: "Are admin accounts (except those specifically allowed) blocked from accessing the internet, email, and web services?",
        originalId: "ML1-RAP-03"
      },
      { 
        id: "L-ML1-AP-04", 
        question: "Are admin accounts that need internet access limited to only what they need for their work?",
        originalId: "ML1-RAP-04"
      },
      { 
        id: "L-ML1-AP-05", 
        question: "Do admin users use separate computers or environments for admin work versus regular work?",
        originalId: "ML1-RAP-05"
      },
      { 
        id: "L-ML1-AP-06", 
        question: "Are regular user accounts prevented from logging into admin environments?",
        originalId: "ML1-RAP-06"
      },
      { 
        id: "L-ML1-AP-07", 
        question: "Are admin accounts (except local admin accounts) prevented from logging into regular user environments?",
        originalId: "ML1-RAP-07"
      }
    ],
    ML2: [
      { 
        id: "L-ML2-AP-01", 
        question: "Do you review and reapprove admin access to systems, applications, and data storage every 12 months?",
        originalId: "ML2-RAP-01"
      },
      { 
        id: "L-ML2-AP-02", 
        question: "Is admin access automatically disabled after 45 days of no use?",
        originalId: "ML2-RAP-02"
      },
      { 
        id: "L-ML2-AP-03", 
        question: "Do you use dedicated admin workstations (not virtual machines) for admin tasks?",
        originalId: "ML2-RAP-03"
      },
      { 
        id: "L-ML2-AP-04", 
        question: "Do you use jump servers (secure gateways) for admin tasks?",
        originalId: "ML2-RAP-04"
      },
      { 
        id: "L-ML2-AP-05", 
        question: "Do you use strong, unique passwords for emergency accounts, local admin accounts, and service accounts?",
        originalId: "ML2-RAP-05"
      },
      { 
        id: "L-ML2-AP-06", 
        question: "Do you log all admin access attempts centrally?",
        originalId: "ML2-RAP-06"
      },
      { 
        id: "L-ML2-AP-07", 
        question: "Do you log all changes to admin accounts and groups centrally?",
        originalId: "ML2-RAP-07"
      },
      { 
        id: "L-ML2-AP-08", 
        question: "Are your admin logs protected from unauthorized changes or deletion?",
        originalId: "ML2-RAP-08"
      },
      { 
        id: "L-ML2-AP-09", 
        question: "Do you analyze admin logs from internet-facing servers regularly to detect security issues?",
        originalId: "ML2-RAP-09"
      },
      { 
        id: "L-ML2-AP-10", 
        question: "Do you analyze security events to identify security incidents?",
        originalId: "ML2-RAP-10"
      },
      { 
        id: "L-ML2-AP-11", 
        question: "Do you report security incidents to your Chief Information Security Officer (or delegate) as soon as possible?",
        originalId: "ML2-RAP-11"
      },
      { 
        id: "L-ML2-AP-12", 
        question: "Do you report security incidents to ASD as soon as possible?",
        originalId: "ML2-RAP-12"
      },
      { 
        id: "L-ML2-AP-13", 
        question: "Do you follow your security incident response plan when incidents are identified?",
        originalId: "ML2-RAP-13"
      }
    ],
    ML3: [
      { 
        id: "L-ML3-AP-01", 
        question: "Is admin access to systems, applications, and data storage limited to only what each admin needs for their work?",
        originalId: "ML3-RAP-01"
      },
      { 
        id: "L-ML3-AP-02", 
        question: "Do you use dedicated Secure Admin Workstations for admin tasks?",
        originalId: "ML3-RAP-02"
      },
      { 
        id: "L-ML3-AP-03", 
        question: "Do you use just-in-time admin access (temporary admin rights only when needed)?",
        originalId: "ML3-RAP-03"
      },
      { 
        id: "L-ML3-AP-04", 
        question: "Is memory integrity protection enabled on your admin workstations?",
        originalId: "ML3-RAP-04"
      },
      { 
        id: "L-ML3-AP-05", 
        question: "Is Local Security Authority protection enabled on your admin workstations?",
        originalId: "ML3-RAP-05"
      },
      { 
        id: "L-ML3-AP-06", 
        question: "Is Credential Guard enabled on your admin workstations?",
        originalId: "ML3-RAP-06"
      },
      { 
        id: "L-ML3-AP-07", 
        question: "Is Remote Credential Guard enabled on your admin workstations?",
        originalId: "ML3-RAP-07"
      },
      { 
        id: "L-ML3-AP-08", 
        question: "Do you analyze admin logs from internal servers regularly to detect security issues?",
        originalId: "ML3-RAP-08"
      },
      { 
        id: "L-ML3-AP-09", 
        question: "Do you analyze admin logs from workstations regularly to detect security issues?",
        originalId: "ML3-RAP-09"
      }
    ]
  },
  [CONTROL_TYPES.APP_CONTROL]: {
    ML1: [
      { 
        id: "L-ML1-AC-01", 
        question: "Do you use application control (allowlisting) on your workstations to control what software can run?",
        originalId: "ML1-AC-01"
      },
      { 
        id: "L-ML1-AC-02", 
        question: "Is application control applied to user profiles and temporary folders used by operating systems, web browsers, and email programs?",
        originalId: "ML1-AC-02"
      },
      { 
        id: "L-ML1-AC-03", 
        question: "Does your application control restrict what can run to only approved programs, libraries, scripts, installers, and control panel items?",
        originalId: "ML1-AC-03"
      }
    ],
    ML2: [
      { 
        id: "L-ML2-AC-01", 
        question: "Do you use application control on your internet-facing servers?",
        originalId: "ML2-AC-01"
      },
      { 
        id: "L-ML2-AC-02", 
        question: "Is application control applied to all locations on your systems, not just user profiles and temporary folders?",
        originalId: "ML2-AC-02"
      },
      { 
        id: "L-ML2-AC-03", 
        question: "Have you implemented Microsoft's recommended list of applications to block?",
        originalId: "ML2-AC-03"
      },
      { 
        id: "L-ML2-AC-04", 
        question: "Do you review and update your application control rules at least once a year?",
        originalId: "ML2-AC-04"
      },
      { 
        id: "L-ML2-AC-05", 
        question: "Do you log all allowed and blocked application events centrally?",
        originalId: "ML2-AC-05"
      },
      { 
        id: "L-ML2-AC-06", 
        question: "Are your application control logs protected from unauthorized changes or deletion?",
        originalId: "ML2-AC-06"
      },
      { 
        id: "L-ML2-AC-07", 
        question: "Do you analyze application control logs from internet-facing servers regularly to detect security issues?",
        originalId: "ML2-AC-07"
      },
      { 
        id: "L-ML2-AC-08", 
        question: "Do you analyze security events to identify security incidents?",
        originalId: "ML2-AC-08"
      },
      { 
        id: "L-ML2-AC-09", 
        question: "Do you report security incidents to your Chief Information Security Officer (or delegate) as soon as possible?",
        originalId: "ML2-AC-09"
      },
      { 
        id: "L-ML2-AC-10", 
        question: "Do you report security incidents to ASD as soon as possible?",
        originalId: "ML2-AC-10"
      },
      { 
        id: "L-ML2-AC-11", 
        question: "Do you follow your security incident response plan when incidents are identified?",
        originalId: "ML2-AC-11"
      }
    ],
    ML3: [
      { 
        id: "L-ML3-AC-01", 
        question: "Do you use application control on your internal servers?",
        originalId: "ML3-AC-01"
      },
      { 
        id: "L-ML3-AC-02", 
        question: "Does your application control restrict what device drivers can run to only approved ones?",
        originalId: "ML3-AC-02"
      },
      { 
        id: "L-ML3-AC-03", 
        question: "Have you implemented Microsoft's recommended list of vulnerable drivers to block?",
        originalId: "ML3-AC-03"
      },
      { 
        id: "L-ML3-AC-04", 
        question: "Do you analyze application control logs from internal servers regularly to detect security issues?",
        originalId: "ML3-AC-04"
      },
      { 
        id: "L-ML3-AC-05", 
        question: "Do you analyze application control logs from workstations regularly to detect security issues?",
        originalId: "ML3-AC-05"
      }
    ]
  },
  [CONTROL_TYPES.OFFICE_MACROS]: {
    ML1: [
      { 
        id: "L-ML1-OM-01", 
        question: "Are Microsoft Office macros disabled for users who don't need them for their work?",
        originalId: "ML1-RM-01"
      },
      { 
        id: "L-ML1-OM-02", 
        question: "Are Microsoft Office macros in files from the internet blocked?",
        originalId: "ML1-RM-02"
      },
      { 
        id: "L-ML1-OM-03", 
        question: "Is antivirus scanning enabled for Microsoft Office macros?",
        originalId: "ML1-RM-03"
      },
      { 
        id: "L-ML1-OM-04", 
        question: "Are users prevented from changing Microsoft Office macro security settings?",
        originalId: "ML1-RM-04"
      }
    ],
    ML2: [
      { 
        id: "L-ML2-OM-01", 
        question: "Are Microsoft Office macros blocked from making system-level changes (Win32 API calls)?",
        originalId: "ML2-RM-01"
      }
    ],
    ML3: [
      { 
        id: "L-ML3-OM-01", 
        question: "Do you only allow Microsoft Office macros that run in a secure environment, are from trusted locations, or are digitally signed by trusted publishers?",
        originalId: "ML3-RM-01"
      },
      { 
        id: "L-ML3-OM-02", 
        question: "Do you check Microsoft Office macros for malicious code before approving them?",
        originalId: "ML3-RM-02"
      },
      { 
        id: "L-ML3-OM-03", 
        question: "Do you restrict who can modify the trusted locations for macros to only those who check macros for malicious code?",
        originalId: "ML3-RM-03"
      },
      { 
        id: "L-ML3-OM-04", 
        question: "Are users prevented from enabling macros signed by untrusted publishers through the Message Bar or Backstage View?",
        originalId: "ML3-RM-04"
      },
      { 
        id: "L-ML3-OM-05", 
        question: "Are users prevented from enabling macros signed with older signature types (non-V3) through the Message Bar or Backstage View?",
        originalId: "ML3-RM-05"
      },
      { 
        id: "L-ML3-OM-06", 
        question: "Do you review and update your list of trusted macro publishers at least once a year?",
        originalId: "ML3-RM-06"
      }
    ]
  },
  [CONTROL_TYPES.APP_HARDENING]: {
    ML1: [
      { 
        id: "L-ML1-AH-01", 
        question: "Have you disabled or removed Internet Explorer 11?",
        originalId: "ML1-UAH-01"
      },
      { 
        id: "L-ML1-AH-02", 
        question: "Are web browsers configured to block Java from the internet?",
        originalId: "ML1-UAH-02"
      },
      { 
        id: "L-ML1-AH-03", 
        question: "Are web browsers configured to block advertisements from the internet?",
        originalId: "ML1-UAH-03"
      },
      { 
        id: "L-ML1-AH-04", 
        question: "Are users prevented from changing web browser security settings?",
        originalId: "ML1-UAH-04"
      }
    ],
    ML2: [
      { 
        id: "L-ML2-AH-01", 
        question: "Are web browsers hardened according to ASD and vendor security guidance?",
        originalId: "ML2-UAH-01"
      },
      { 
        id: "L-ML2-AH-02", 
        question: "Is Microsoft Office blocked from creating child processes?",
        originalId: "ML2-UAH-02"
      },
      { 
        id: "L-ML2-AH-03", 
        question: "Is Microsoft Office blocked from creating executable content?",
        originalId: "ML2-UAH-03"
      },
      { 
        id: "L-ML2-AH-04", 
        question: "Is Microsoft Office blocked from injecting code into other processes?",
        originalId: "ML2-UAH-04"
      },
      { 
        id: "L-ML2-AH-05", 
        question: "Is Microsoft Office configured to prevent activation of Object Linking and Embedding packages?",
        originalId: "ML2-UAH-05"
      },
      { 
        id: "L-ML2-AH-06", 
        question: "Are office productivity suites hardened according to ASD and vendor security guidance?",
        originalId: "ML2-UAH-06"
      },
      { 
        id: "L-ML2-AH-07", 
        question: "Are users prevented from changing office productivity suite security settings?",
        originalId: "ML2-UAH-07"
      },
      { 
        id: "L-ML2-AH-08", 
        question: "Is PDF software blocked from creating child processes?",
        originalId: "ML2-UAH-08"
      },
      { 
        id: "L-ML2-AH-09", 
        question: "Is PDF software hardened according to ASD and vendor security guidance?",
        originalId: "ML2-UAH-09"
      },
      { 
        id: "L-ML2-AH-10", 
        question: "Are users prevented from changing PDF software security settings?",
        originalId: "ML2-UAH-10"
      },
      { 
        id: "L-ML2-AH-11", 
        question: "Do you log PowerShell module logging, script block logging, and transcription events centrally?",
        originalId: "ML2-UAH-11"
      },
      { 
        id: "L-ML2-AH-12", 
        question: "Do you log command line process creation events centrally?",
        originalId: "ML2-UAH-12"
      },
      { 
        id: "L-ML2-AH-13", 
        question: "Are your application hardening logs protected from unauthorized changes or deletion?",
        originalId: "ML2-UAH-13"
      },
      { 
        id: "L-ML2-AH-14", 
        question: "Do you analyze application hardening logs from internet-facing servers regularly to detect security issues?",
        originalId: "ML2-UAH-14"
      },
      { 
        id: "L-ML2-AH-15", 
        question: "Do you analyze security events to identify security incidents?",
        originalId: "ML2-UAH-15"
      },
      { 
        id: "L-ML2-AH-16", 
        question: "Do you report security incidents to your Chief Information Security Officer (or delegate) as soon as possible?",
        originalId: "ML2-UAH-16"
      },
      { 
        id: "L-ML2-AH-17", 
        question: "Do you report security incidents to ASD as soon as possible?",
        originalId: "ML2-UAH-17"
      },
      { 
        id: "L-ML2-AH-18", 
        question: "Do you follow your security incident response plan when incidents are identified?",
        originalId: "ML2-UAH-18"
      }
    ],
    ML3: [
      { 
        id: "L-ML3-AH-01", 
        question: "Have you disabled or removed .NET Framework 3.5 (includes .NET 2.0 and 3.0)?",
        originalId: "ML3-UAH-01"
      },
      { 
        id: "L-ML3-AH-02", 
        question: "Have you disabled or removed Windows PowerShell 2.0?",
        originalId: "ML3-UAH-02"
      },
      { 
        id: "L-ML3-AH-03", 
        question: "Is PowerShell configured to use Constrained Language Mode?",
        originalId: "ML3-UAH-03"
      },
      { 
        id: "L-ML3-AH-04", 
        question: "Do you analyze application hardening logs from internal servers regularly to detect security issues?",
        originalId: "ML3-UAH-04"
      },
      { 
        id: "L-ML3-AH-05", 
        question: "Do you analyze application hardening logs from workstations regularly to detect security issues?",
        originalId: "ML3-UAH-05"
      }
    ]
  },
  [CONTROL_TYPES.BACKUPS]: {
    ML1: [
      { 
        id: "L-ML1-BK-01", 
        question: "Do you perform and keep backups of your data, applications, and settings based on how important they are to your business?",
        originalId: "ML1-RB-01"
      },
      { 
        id: "L-ML1-BK-02", 
        question: "Are your backups synchronized so you can restore everything to the same point in time?",
        originalId: "ML1-RB-02"
      },
      { 
        id: "L-ML1-BK-03", 
        question: "Are your backups stored securely and protected against disasters?",
        originalId: "ML1-RB-03"
      },
      { 
        id: "L-ML1-BK-04", 
        question: "Do you test restoring from backups as part of your disaster recovery exercises?",
        originalId: "ML1-RB-04"
      },
      { 
        id: "L-ML1-BK-05", 
        question: "Are regular users prevented from accessing other users' backups?",
        originalId: "ML1-RB-05"
      },
      { 
        id: "L-ML1-BK-06", 
        question: "Are regular users prevented from changing or deleting backups?",
        originalId: "ML1-RB-06"
      }
    ],
    ML2: [
      { 
        id: "L-ML2-BK-01", 
        question: "Are admin users (except backup admins) prevented from accessing other users' backups?",
        originalId: "ML2-RB-01"
      },
      { 
        id: "L-ML2-BK-02", 
        question: "Are admin users (except backup admins) prevented from changing or deleting backups?",
        originalId: "ML2-RB-02"
      }
    ],
    ML3: [
      { 
        id: "L-ML3-BK-01", 
        question: "Are regular users prevented from accessing their own backups?",
        originalId: "ML3-RB-01"
      },
      { 
        id: "L-ML3-BK-02", 
        question: "Are admin users (except backup admins) prevented from accessing their own backups?",
        originalId: "ML3-RB-02"
      },
      { 
        id: "L-ML3-BK-03", 
        question: "Are backup admin accounts prevented from changing or deleting backups during their retention period?",
        originalId: "ML3-RB-03"
      }
    ]
  }
};
